import { AgentInteractions } from "../models";

// Guards against a stale read resurrecting a just-closed interaction.
//
// The backend keeps returning a messaging interaction as active in GET /interactions for several
// seconds after it has already pushed the ActiveMessagingChanged "Removed" for it (the inactivity
// close commits later — observed ~6.5s between the removal push and the close). Any refetch in that
// window (invalidateQueries, refetchOnMount on a tab switch, a SignalR reconnect) would otherwise
// overwrite the cache with the interaction still present, re-adding it to the interaction bar/list
// until a much later refetch. We remember the ids the client was explicitly told to close and drop
// them from the ACTIVE list of any fetch result for a short window, so a stale read cannot undo a
// removal the client already applied. Callers forget an id when it legitimately comes back
// (re-offered/accepted). (CC4ALL-9525)
const RECENTLY_CLOSED_TTL_MS = 15000;
const recentlyClosedInteractions = new Map<string, number>();

export const markInteractionRecentlyClosed = (interactionId: string) => {
	// Guards against a falsy id (e.g. WrapUpStopped.mediaId, typed non-optional but not
	// runtime-validated from the SignalR payload) tombstoning an undefined key.
	if (!interactionId) {
		return;
	}

	recentlyClosedInteractions.set(
		interactionId,
		Date.now() + RECENTLY_CLOSED_TTL_MS
	);
};

export const forgetRecentlyClosedInteraction = (interactionId: string) => {
	recentlyClosedInteractions.delete(interactionId);
};

// Prunes expired entries and returns the ids still within their window.
const collectRecentlyClosedIds = (): Set<string> => {
	const now = Date.now();
	const ids = new Set<string>();

	recentlyClosedInteractions.forEach((expiresAt, id) => {
		if (expiresAt <= now) {
			recentlyClosedInteractions.delete(id);
		} else {
			ids.add(id);
		}
	});

	return ids;
};

// Drops recently-closed ids from the active list only. Waiting is left untouched so a genuine
// re-offer (which surfaces in waiting first) is never hidden.
export const dropRecentlyClosedInteractions = (
	interactions: AgentInteractions
): AgentInteractions => {
	const closedIds = collectRecentlyClosedIds();

	if (closedIds.size === 0 || !interactions.active) {
		return interactions;
	}

	return {
		...interactions,
		active: interactions.active.filter((i) => !closedIds.has(i.id)),
	};
};
